import { supabase } from './supabase';
import { EmployeeLeaveBalance, LeaveBalanceHistory, AutomaticAbsenceSettings } from '../types';

export const leaveBalanceService = {
  // Get leave balance for an employee for a specific year
  async getEmployeeLeaveBalance(employeeId: string, year: number): Promise<EmployeeLeaveBalance | null> {
    try {
      const { data, error } = await supabase
        .from('employee_leave_balances')
        .select('*')
        .eq('employee_id', employeeId)
        .eq('year', year)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching employee leave balance:', error);
      throw error;
    }
  },

  // Get all leave balances for an employee
  async getEmployeeLeaveBalances(employeeId: string): Promise<EmployeeLeaveBalance[]> {
    try {
      const { data, error } = await supabase
        .from('employee_leave_balances')
        .select('*')
        .eq('employee_id', employeeId)
        .order('year', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching employee leave balances:', error);
      throw error;
    }
  },

  // Get leave balances for all employees for a specific year
  async getAllEmployeeLeaveBalances(year: number): Promise<EmployeeLeaveBalance[]> {
    try {
      const { data, error } = await supabase
        .from('employee_leave_balances')
        .select(`
          *,
          employees!inner(
            id,
            first_name,
            last_name,
            email,
            department,
            position
          )
        `)
        .eq('year', year)
        .order('employees(last_name)');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching all employee leave balances:', error);
      throw error;
    }
  },

  // Initialize leave balance for an employee
  async initializeEmployeeLeaveBalance(employeeId: string, year: number): Promise<EmployeeLeaveBalance> {
    try {
      const { data, error } = await supabase
        .from('employee_leave_balances')
        .insert({
          employee_id: employeeId,
          year: year
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error initializing employee leave balance:', error);
      throw error;
    }
  },

  // Update leave balance
  async updateLeaveBalance(
    employeeId: string, 
    year: number, 
    updates: Partial<EmployeeLeaveBalance>
  ): Promise<EmployeeLeaveBalance> {
    try {
      const { data, error } = await supabase
        .from('employee_leave_balances')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('employee_id', employeeId)
        .eq('year', year)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating leave balance:', error);
      throw error;
    }
  },

  // Get leave balance history for an employee
  async getLeaveBalanceHistory(employeeId: string): Promise<LeaveBalanceHistory[]> {
    try {
      const { data, error } = await supabase
        .from('leave_balance_history')
        .select(`
          *,
          leave_requests(
            id,
            start_date,
            end_date,
            reason
          )
        `)
        .eq('employee_id', employeeId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching leave balance history:', error);
      throw error;
    }
  },

  // Check if employee has sufficient leave balance
  async checkLeaveBalance(
    employeeId: string, 
    leaveType: string, 
    daysRequested: number, 
    year: number
  ): Promise<{ hasBalance: boolean; remainingDays: number; message: string }> {
    try {
      console.log('checkLeaveBalance: Input params:', { employeeId, leaveType, daysRequested, year });
      
      const balance = await this.getEmployeeLeaveBalance(employeeId, year);
      console.log('checkLeaveBalance: Retrieved balance:', balance);
      
      if (!balance) {
        console.log('checkLeaveBalance: No balance found');
        return {
          hasBalance: false,
          remainingDays: 0,
          message: 'No leave balance found for this year'
        };
      }

      let remainingDays = 0;
      let hasBalance = false;
      let message = '';

      switch (leaveType) {
        case 'vacation':
          remainingDays = balance.vacation_days_remaining;
          hasBalance = remainingDays >= daysRequested;
          console.log(`checkLeaveBalance: Vacation - remaining: ${remainingDays}, requested: ${daysRequested}, hasBalance: ${hasBalance}`);
          message = hasBalance 
            ? `Sufficient vacation days available (${remainingDays} remaining)`
            : `You have no sufficient leave to request for this Vacation Leave`;
          break;
        case 'sick':
          remainingDays = balance.sick_days_remaining;
          hasBalance = remainingDays >= daysRequested;
          console.log(`checkLeaveBalance: Sick - remaining: ${remainingDays}, requested: ${daysRequested}, hasBalance: ${hasBalance}`);
          message = hasBalance 
            ? `Sufficient sick days available (${remainingDays} remaining)`
            : `You have no sufficient leave to request for this Sick Leave`;
          break;
        case 'personal':
          remainingDays = balance.personal_days_remaining;
          hasBalance = remainingDays >= daysRequested;
          console.log(`checkLeaveBalance: Personal - remaining: ${remainingDays}, requested: ${daysRequested}, hasBalance: ${hasBalance}`);
          message = hasBalance 
            ? `Sufficient personal days available (${remainingDays} remaining)`
            : `You have no sufficient leave to request for this Personal Leave`;
          break;
        case 'emergency':
          remainingDays = balance.emergency_days_remaining;
          hasBalance = remainingDays >= daysRequested;
          console.log(`checkLeaveBalance: Emergency - remaining: ${remainingDays}, requested: ${daysRequested}, hasBalance: ${hasBalance}`);
          message = hasBalance 
            ? `Sufficient emergency days available (${remainingDays} remaining)`
            : `You have no sufficient leave to request for this Emergency Leave`;
          break;
        case 'maternity':
          remainingDays = balance.maternity_days_remaining;
          hasBalance = remainingDays >= daysRequested;
          console.log(`checkLeaveBalance: Maternity - remaining: ${remainingDays}, requested: ${daysRequested}, hasBalance: ${hasBalance}`);
          message = hasBalance 
            ? `Sufficient maternity days available (${remainingDays} remaining)`
            : `You have no sufficient leave to request for this Maternity Leave`;
          break;
        case 'paternity':
          remainingDays = balance.paternity_days_remaining;
          hasBalance = remainingDays >= daysRequested;
          console.log(`checkLeaveBalance: Paternity - remaining: ${remainingDays}, requested: ${daysRequested}, hasBalance: ${hasBalance}`);
          message = hasBalance 
            ? `Sufficient paternity days available (${remainingDays} remaining)`
            : `You have no sufficient leave to request for this Paternity Leave`;
          break;
        default:
          // For other leave types, allow as unpaid leave
          hasBalance = true;
          remainingDays = 999; // Unlimited for unpaid leave
          console.log(`checkLeaveBalance: Default case - leave type: ${leaveType}, allowing as unpaid`);
          message = 'This will be processed as unpaid leave';
      }

      const result = { hasBalance, remainingDays, message };
      console.log('checkLeaveBalance: Final result:', result);
      return result;
    } catch (error) {
      console.error('Error checking leave balance:', error);
      throw error;
    }
  },

  // Calculate working days between two dates (excluding weekends)
  calculateWorkingDays(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    let workingDays = 0;
    
    const currentDate = new Date(start);
    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay();
      // 0 = Sunday, 6 = Saturday
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return workingDays;
  },

  // Get automatic absence settings
  async getAutomaticAbsenceSettings(): Promise<AutomaticAbsenceSettings | null> {
    try {
      const { data, error } = await supabase
        .from('automatic_absence_settings')
        .select('*')
        .eq('enabled', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching automatic absence settings:', error);
      throw error;
    }
  },

  // Update automatic absence settings
  async updateAutomaticAbsenceSettings(settings: Partial<AutomaticAbsenceSettings>): Promise<AutomaticAbsenceSettings> {
    try {
      const { data, error } = await supabase
        .from('automatic_absence_settings')
        .update({
          ...settings,
          updated_at: new Date().toISOString()
        })
        .eq('enabled', true)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating automatic absence settings:', error);
      throw error;
    }
  },

  // Manually trigger automatic absence marking
  async triggerAutomaticAbsenceMarking(): Promise<{ markedAbsent: number; message: string }> {
    try {
      const { data, error } = await supabase
        .rpc('mark_automatic_absences');

      if (error) throw error;

      const markedAbsent = data || 0;
      return {
        markedAbsent,
        message: `${markedAbsent} employees marked as absent automatically`
      };
    } catch (error) {
      console.error('Error triggering automatic absence marking:', error);
      throw error;
    }
  },

  // Get leave summary for dashboard
  async getLeaveSummary(year: number): Promise<{
    totalEmployees: number;
    totalLeaveRequests: number;
    pendingRequests: number;
    approvedRequests: number;
    rejectedRequests: number;
    totalDaysUsed: number;
    totalDaysRemaining: number;
  }> {
    try {
      // Get leave requests summary
      const { data: leaveRequests, error: leaveError } = await supabase
        .from('leave_requests')
        .select('status, days_approved')
        .gte('start_date', `${year}-01-01`)
        .lte('start_date', `${year}-12-31`);

      if (leaveError) throw leaveError;

      // Get leave balances summary
      const { data: balances, error: balanceError } = await supabase
        .from('employee_leave_balances')
        .select(`
          vacation_days_used,
          sick_days_used,
          personal_days_used,
          emergency_days_used,
          vacation_days_remaining,
          sick_days_remaining,
          personal_days_remaining,
          emergency_days_remaining
        `)
        .eq('year', year);

      if (balanceError) throw balanceError;

      const totalEmployees = balances?.length || 0;
      const totalLeaveRequests = leaveRequests?.length || 0;
      const pendingRequests = leaveRequests?.filter(req => req.status === 'pending').length || 0;
      const approvedRequests = leaveRequests?.filter(req => req.status === 'approved').length || 0;
      const rejectedRequests = leaveRequests?.filter(req => req.status === 'rejected').length || 0;
      
      const totalDaysUsed = balances?.reduce((sum: number, balance: any) =>
        sum + (balance.vacation_days_used || 0) +
        (balance.sick_days_used || 0) +
        (balance.personal_days_used || 0) +
        (balance.emergency_days_used || 0), 0) || 0;

      const totalDaysRemaining = balances?.reduce((sum: number, balance: any) =>
        sum + (balance.vacation_days_remaining || 0) +
        (balance.sick_days_remaining || 0) +
        (balance.personal_days_remaining || 0) +
        (balance.emergency_days_remaining || 0), 0) || 0;

      return {
        totalEmployees,
        totalLeaveRequests,
        pendingRequests,
        approvedRequests,
        rejectedRequests,
        totalDaysUsed,
        totalDaysRemaining
      };
    } catch (error) {
      console.error('Error fetching leave summary:', error);
      throw error;
    }
  }
};